<script setup lang="ts">
import { lectureService } from '@/api/services/lecture-service'
import CheckIcon from '@/assets/svgs/check.svg'
import CheckTintIcon from '@/assets/svgs/check_tint.svg'
import HeartIcon from '@/assets/svgs/heart.svg'
import HeartFilledIcon from '@/assets/svgs/heart_fill.svg'
import BaseSmallButton from '@/components/base/BaseSmallButton.vue'
import BaseTag from '@/components/base/BaseTag.vue'
import { useIsMobile } from '@/composables/useMediaQuery'
import { useModal } from '@/composables/useModal'
import { requestAuthentication } from '@/sdk/bootpay-sdk'
import { useAuthStore } from '@/stores/auth'
import type { Lecture } from '@/types/lecture.types'
import { getKoreanLevelFromLecture } from '@/utils/lectureLevel'
import Player from '@vimeo/player'
import { computed, defineEmits, nextTick, ref } from 'vue'
import BaseModal from './BaseModal.vue'
import { useToast } from '@/composables/useToast'

const props = defineProps<{
  borderRadius?: number
  lecture: Lecture
  showLevel?: boolean
  showPlayButton?: boolean
  showViewableUntil?: boolean
  maxWidth?: number
  videoClick?: () => void
}>()

const isMobile = useIsMobile()
const authStore = useAuthStore()
const toast = useToast()
const { isOpen, open, close } = useModal()

const isHovered = ref(false)
const isPurchaseModalOpen = ref(false)
const isLiked = ref(props.lecture.bookmarked)
const purchaseSubscription = ref(true)
const isPurchaseTermsAccepted = ref(false)

const emit = defineEmits<{
  (e: 'videoClick', videoId: number): void
}>()

const formattedPrice = computed(() => {
  return props.lecture.price.toLocaleString()
})

const formattedSubscriptionPrice = computed(() => {
  return props.lecture.subscriptionPrice.toLocaleString()
})

const vimeoUrl = computed(() => {
  const baseUrl = props.lecture.thumbnailVimeoUrl
  const hasParams = baseUrl.includes('?')
  return `${baseUrl}${hasParams ? '&' : '?'}muted=1&controls=0`
})

const maxWidth = computed(() => {
  return props.maxWidth ? props.maxWidth + `px` : '847px'
})

const computedHeartSvg = computed(() => {
  return isLiked.value ? HeartFilledIcon : HeartIcon
})

const computedBorderRadius = computed(() => {
  if (props.borderRadius === 0) {
    return '0px'
  }

  return props.borderRadius ? props.borderRadius + `px` : '8px'
})

const getViewableStatus = () => {
  // playable이 false면 시청기간 만료
  if (props.lecture.playable === false) {
    return { text: '시청기간 만료', class: 'viewable-expired' }
  }

  // 평생소장 조건 (permanent이 true면 평생소장)
  if (props.lecture.permanent === true) {
    return { text: '평생소장', class: 'viewable-lifetime' }
  }

  if (props.lecture.expiresAt != null) {
    const dateObj = new Date(props.lecture.expiresAt)

    const year = dateObj.getFullYear().toString().slice(2)
    const month = (dateObj.getMonth() + 1).toString().padStart(2, '0')
    const day = dateObj.getDate().toString().padStart(2, '0')

    return {
      text: `${year}.${month}.${day}까지 시청가능`,
      class: 'viewable-active',
    }
  }

  return { text: '', class: '' }
}

const viewableStatus = computed(() => {
  return getViewableStatus()
})

const koreanLevel = getKoreanLevelFromLecture(props.lecture)

const toggleLike = async (event: Event) => {
  event.stopPropagation()
  const isBookmarked = await lectureService.toggleBookmark(props.lecture.idx)
  isLiked.value = isBookmarked
}

const handleVideoClick = () => {


  if (props.lecture.playable) {
    emit('videoClick', props.lecture.idx)
  } else {
    if (authStore.state.account?.authDone) {
      openPurchaseModal()
    } else {
      openAuthenticationModal()
    }
  }
}

const handleMouseEnter = async () => {
  if (props.lecture.thumbnailType === 'VIDEO' && props.lecture.thumbnailVimeoUrl) {
    const player = new Player(`vimeo-player-thumbnail-${props.lecture.idx}`)
    isHovered.value = true
    await nextTick()

    if (player) {
      player
        .ready()
        .then(() => {
          player.play()
        })
        .catch((error) => {
          console.error('Vimeo player ready error:', error)
        })
    }
  }
}

const handleMouseLeave = async () => {
  if (props.lecture.thumbnailType === 'VIDEO' && props.lecture.thumbnailVimeoUrl) {
    const player = new Player(`vimeo-player-thumbnail-${props.lecture.idx}`)

    if (player) {
      try {
        await player.ready()
        await player.setCurrentTime(0)
        await player.pause()
      } catch (error) {
        console.error('Vimeo player error:', error)
      } finally {
        isHovered.value = false
      }
    } else {
      isHovered.value = false
    }
  }
}

const openAuthenticationModal = () => {
  open()
}

const openPurchaseModal = () => {
  isPurchaseTermsAccepted.value = false
  isPurchaseModalOpen.value = true
}

const closeAuthenticationModal = () => {
  close()
}

const closePurchaseModal = () => {
  isPurchaseModalOpen.value = false
}

const handlePurchaseConfirm = async () => {
  if (isPurchaseTermsAccepted.value) {
    isPurchaseModalOpen.value = false

    // 먼저 빈 팝업을 엽니다
    const popup = window.open(
      'about:blank',
      '_blank',
      'width=650,height=825,resizable=no,scrollbars=yes',
    )

    try {
      // 비동기 작업 수행
      const order = await lectureService.createOrder(props.lecture.idx, purchaseSubscription.value)

      // 팝업이 열려있고 닫히지 않았다면 URL 변경
      if (popup && !popup.closed) {
        popup.location.href = `${import.meta.env.VITE_BASE_URL}/lecture/order?orderId=${order.orderId}&redirectUrl=${import.meta.env.VITE_APP_HOST}/video/${props.lecture.idx}&cancelRedirectUrl=${window.location.href}`
      }
    } catch (error) {
      // 에러 발생 시 팝업 닫기
      if (popup && !popup.closed) {
        popup.close()
      }
      console.error(error)
      toast.showToast('주문 생성에 실패했습니다.', 'error')
    }
  }
}

const handleAuthenticationConfirm = async () => {
  if (isOpen.value) {
    closeAuthenticationModal()
    const isSuccess = await requestAuthentication()
    if (isSuccess) {
      await authStore.getUserInfo()
      toast.showToast('본인인증이 완료되었습니다.', 'success')
      handleVideoClick()
    } else {
      toast.showToast('본인인증에 실패했습니다.', 'error')
    }
  }
}
</script>

<template>
  <div class="base-video-wrapper">
    <div class="base-video">
      <div
        class="base-video-image"
        @click="handleVideoClick"
        @mouseenter="handleMouseEnter"
        @mouseleave="handleMouseLeave"
      >
        <img
          v-if="props.lecture.thumbnailType !== 'VIDEO'"
          :src="props.lecture.thumbnailImageUrl"
        />
        <iframe
          v-if="props.lecture.thumbnailType === 'VIDEO' && props.lecture.thumbnailVimeoUrl"
          :id="`vimeo-player-thumbnail-${props.lecture.idx}`"
          :src="vimeoUrl"
          frameborder="0"
          allow="autoplay; fullscreen"
          allowfullscreen
        ></iframe>
      </div>
      <div class="base-video-course-tag">
        <BaseTag :text="props.lecture.courseName" />
      </div>
      <BaseTag
        v-if="props.lecture.free || props.lecture.freeGroup"
        class="base-video-free-tag"
        text="무료"
      />
      <div
        v-if="props.showViewableUntil"
        class="base-video-viewable-until sub-md"
        :class="viewableStatus.class"
      >
        {{ viewableStatus.text }}
      </div>
      <div class="base-video-info-container">
        <div class="base-video-info">
          <div v-if="props.showLevel" class="base-video-info-level body-sm white">
            {{ koreanLevel }}
          </div>
          <div class="base-video-info-title body-lg white">
            {{ props.lecture.name }}
          </div>
        </div>
        <div class="base-video-button-container">
          <img
            class="base-video-button-heart svg-24"
            :class="{ 'svg-white': !isLiked }"
            :src="computedHeartSvg"
            alt="heart"
            @click="toggleLike"
          />
          <BaseSmallButton
            class="base-video-button-play"
            v-if="props.showPlayButton && !isMobile"
            text="강좌 보러가기"
            type="main"
            @click="handleVideoClick"
          />
        </div>
      </div>
      <BaseSmallButton
        class="base-video-button-play"
        v-if="props.showPlayButton && isMobile"
        text="강좌 보러가기"
        type="main"
        @click="handleVideoClick"
      />
    </div>

    <BaseModal
      :isOpen="isOpen"
      title="본인인증"
      confirmText="확인"
      cancelText="취소"
      @close="closeAuthenticationModal"
      @confirm="handleAuthenticationConfirm"
    >
      <div class="body-sm white" style="margin-bottom: 28px">
        상품 구매를 위해 최초 1회 본인인증을 진행합니다.
      </div>
    </BaseModal>
    <BaseModal
      :isOpen="isPurchaseModalOpen"
      title="구매하기"
      confirmText="구매하기"
      cancelText="취소"
      @close="closePurchaseModal"
      @confirm="handlePurchaseConfirm"
      :disabled="!isPurchaseTermsAccepted"
    >
      <div class="purchase-modal-content">
        <div class="select-purchase-type">
          <div
            class="select-purchase-type-item cursor-pointer"
            :class="{ selected: purchaseSubscription }"
            @click="purchaseSubscription = true"
          >
            <div class="select-purchase-type-item-title body-lg white">
              [30일]{{ props.lecture.name }}
            </div>
            <div class="select-purchase-type-item-price body-sm white">
              {{ formattedSubscriptionPrice }}원
            </div>
          </div>
          <div
            class="select-purchase-type-item cursor-pointer"
            :class="{ selected: !purchaseSubscription }"
            @click="purchaseSubscription = false"
          >
            <div class="select-purchase-type-item-title body-lg white">
              [평생소장]{{ props.lecture.name }}
            </div>
            <div class="select-purchase-type-item-price body-sm white">{{ formattedPrice }}원</div>
          </div>
        </div>
        <div
          class="purchase-terms cursor-pointer"
          @click="isPurchaseTermsAccepted = !isPurchaseTermsAccepted"
        >
          <img
            class="svg-24 cursor-pointer"
            :class="{ 'svg-gray': !isPurchaseTermsAccepted }"
            :src="isPurchaseTermsAccepted ? CheckTintIcon : CheckIcon"
            alt="check"
          />
          <div class="purchase-terms-text body-sm white cursor-pointer">
            이용안내 및 결제 진행에 동의합니다.
          </div>
        </div>
      </div>
    </BaseModal>
  </div>
</template>

<style scoped>
.base-video {
  width: auto;
  height: auto;
  min-width: 413px;
  max-width: v-bind(maxWidth);
  min-height: 232px;
  max-height: 477px;
  position: relative;
  border-radius: 8px;
}

.base-video-image {
  width: 100%;
  min-width: auto;
  min-height: auto;
  border-radius: v-bind(computedBorderRadius);
  overflow: hidden;
  aspect-ratio: 16 / 9;
  position: relative;
  cursor: pointer;
}

.base-video-image::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0) 66%, rgba(0, 0, 0, 0.8) 92.5%);
  pointer-events: none;
}

.base-video-image img {
  width: 100%;
  min-width: auto;
  min-height: auto;
  aspect-ratio: 16 / 9;
  object-fit: cover;
  display: block;
}

.base-video-course-tag {
  position: absolute;
  top: 12px;
  left: 12px;
}

.base-video-viewable-until {
  position: absolute;
  top: 17px;
  right: 12px;
  border-radius: 4px;
}

.viewable-active {
  color: var(--tint-500);
}

.viewable-lifetime {
  color: var(--white);
}

.viewable-expired {
  color: var(--gray-600);
}

.base-video-free-tag {
  position: absolute;
  top: 12px;
  right: 12px;
  background-color: var(--gray-800);
}

.base-row {
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: start;
  justify-content: start;
  gap: 20px;
}

.base-row-item {
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: start;
  justify-content: start;
  gap: 29px;
  background-color: var(--gray-800);
  border-radius: 8px;
  padding: 20px;
  box-sizing: border-box;
}

.base-video-info-container {
  display: flex;
  flex-direction: row;
  align-items: start;
  justify-content: start;
}

.base-video-info {
  position: absolute;
  bottom: 12px;
  left: 12px;
  display: flex;
  flex-direction: column;
  align-items: start;
  justify-content: start;
  gap: 6px;
}

.base-video-button-container {
  position: absolute;
  align-self: flex-end;
  bottom: 12px;
  right: 12px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 20px;
}

.base-video-button-heart {
  width: 24px;
  height: 24px;
  cursor: pointer;
}

.base-video-image iframe {
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  object-fit: cover;
  pointer-events: none;
}

.purchase-modal-content {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: start;
  justify-content: start;
  gap: 20px;
}

.select-purchase-type {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: start;
  justify-content: start;
  gap: 12px;
}

.select-purchase-type-item {
  width: 100%;
  height: 92px;
  background-color: var(--gray-800);
  border-radius: 8px;
  padding: 12px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: start;
  justify-content: center;
  gap: 4px;
}

.select-purchase-type-item.selected {
  background-color: var(--tint-500);
}

.select-purchase-type-item-title {
  margin-bottom: 4px;
}

.purchase-terms {
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
}

.purchase-terms-text {
  flex: 1;
}

@media (max-width: 1365px) {
  .base-video {
    width: 100%;
    min-width: auto;
    min-height: auto;
    display: flex;
    flex-direction: column;
    align-items: start;
    justify-content: start;
  }

  .base-video-image {
    aspect-ratio: 375 / 210;
    border-radius: 0px;
  }

  .base-video-course-tag {
    position: absolute;
    top: 12px;
    left: 20px;
  }

  .base-video-viewable-until {
    top: 17px;
    right: 20px;
  }

  .base-video-free-tag {
    top: 12px;
    right: 20px;
  }

  .base-video-info {
    position: static;
    bottom: 20px;
    left: 12px;
    display: flex;
    flex-direction: column-reverse;
    align-items: start;
    justify-content: start;
    gap: 6px;
  }

  .base-video-button-container {
    position: static;
    align-self: start;
    bottom: 20px;
    right: 20px;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: 20px;
  }

  .base-video-info-container {
    width: 100%;
    flex-direction: row;
    align-items: start;
    justify-content: space-between;
    padding: 12px 20px;
  }

  .base-video-button-play {
    width: calc(100% - 40px);
    position: static;
    align-self: center;
  }
}
</style>
